import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { server } from "../../server";


const getConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };
};

// Async thunk for creating a sale
export const createSale = (saleData) => async (dispatch) => {
  try {
    dispatch({ type: 'CREATE_SALE_REQUEST' });

    // Get the user from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    const token = localStorage.getItem('token');

    // Check if this is a file upload (FormData)
    const isFileUpload = saleData instanceof FormData;
    
    let config;
    
    if (isFileUpload) {
      // For file uploads, we need to use multipart/form-data
      config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Append user ID to FormData
      saleData.append('user', user._id);
    } else {
      // For regular JSON data
      config = getConfig();
      
      // Add the user ID to the sale data
      saleData.user = user._id;
    }

    const response = await axios.post(
      `${server}/sales/create-sale`,
      saleData,
      config
    );

    if (response.data.success) {
      dispatch({
        type: 'CREATE_SALE_SUCCESS',
        payload: response.data.sale,
      });
      return response.data;
    }
  } catch (error) {
    dispatch({
      type: 'CREATE_SALE_FAIL',
      payload: error.response?.data?.message,
    });
    throw error;
  }
};

// Async thunk for fetching sales by shop
export const fetchSalesByShop = createAsyncThunk(
  "sales/fetchSalesByShop",
  async (shopId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${server}/sales/shop/${shopId}`, getConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch cash sales
export const fetchCashSales = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_CASH_SALES_REQUEST" });

    const shop = typeof shopId === 'object' ? shopId._id : shopId;

    const response = await axios.get(
      `${server}/sales/cash-sales/${shop}`,
      getConfig()
    );
    if (response.data.success) {
      dispatch({
        type: "FETCH_CASH_SALES_SUCCESS",
        payload: response.data
      });
    } else {
      throw new Error(response.data.message || 'Failed to fetch cash sales');
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching cash sales:", error);
    dispatch({
      type: "FETCH_CASH_SALES_FAILURE",
      payload: error.message
    });
    throw error;
  }
};

// Update payment
export const updatePayment = createAsyncThunk(
  "sales/updatePayment",
  async ({ saleId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${server}/sales/pay-debt/${saleId}`,
        paymentData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Debug action to check all sales
export const debugSales = (shopId) => async (dispatch) => {
  try {
    const shop = typeof shopId === 'object' ? shopId._id : shopId;
    const response = await axios.get(
      `${server}/sales/debug-sales/${shop}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching debug sales:", error);
    throw error;
  }
};

// Fetch bank sales
export const fetchBankSales = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: 'FETCH_BANK_SALES_REQUEST' });

    const shop = typeof shopId === 'object' ? shopId._id : shopId;

    const response = await axios.get(
      `${server}/sales/sales/bank/${shop}`,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: 'FETCH_BANK_SALES_SUCCESS',
        payload: response.data
      });
    }
  } catch (error) {
    console.error('Error fetching bank sales:', error);
    dispatch({
      type: 'FETCH_BANK_SALES_FAILURE',
      payload: error.response?.data?.message || 'Error fetching bank sales'
    });
  }
};

// Fetch credit sales
export const fetchCreditSales = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_CREDIT_SALES_REQUEST" });

    const shop = typeof shopId === 'object' ? shopId._id : shopId;

    const response = await axios.get(
      `${server}/sales/credit-sales/${shop}`,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: "FETCH_CREDIT_SALES_SUCCESS",
        payload: response.data
      });
    } else {
      throw new Error(response.data.message || 'Failed to fetch credit sales');
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching credit sales:", error);
    dispatch({
      type: "FETCH_CREDIT_SALES_FAILURE",
      payload: error.message
    });
    throw error;
  }
};

// Pay remaining amount
export const payRemainingAmount = (saleId, paymentData) => async (dispatch) => {
  try {
    dispatch({ type: "PAY_REMAINING_REQUEST" });

    const response = await axios.post(
      `${server}/sales/pay-remaining/${saleId}`,
      paymentData,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: "PAY_REMAINING_SUCCESS",
        payload: response.data
      });

      // Refresh credit sales after payment
      await dispatch(fetchCreditSales(paymentData.shop_id));

      return response.data;
    } else {
      throw new Error(response.data.message || 'Failed to process payment');
    }
  } catch (error) {
    console.error("Error processing payment:", error);
    dispatch({
      type: "PAY_REMAINING_FAILURE",
      payload: error.message
    });
    throw error;
  }
};

// Add this new action
export const fetchDebtClients = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_DEBT_CLIENTS_REQUEST" });

    const response = await axios.get(
      `${server}/sales/debt-clients/${shopId}`,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: "FETCH_DEBT_CLIENTS_SUCCESS",
        payload: response.data.debtClients
      });
      return response.data.debtClients;
    }
  } catch (error) {
    dispatch({
      type: "FETCH_DEBT_CLIENTS_FAILURE",
      payload: error.response?.data?.message || "Error fetching debt clients"
    });
    throw error;
  }
};

// Add delete sale action
export const deleteSale = (saleId) => async (dispatch) => {
  try {
    dispatch({ type: "DELETE_SALE_REQUEST" });

    const response = await axios.delete(
      `${server}/sales/delete/${saleId}`,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: "DELETE_SALE_SUCCESS",
        payload: saleId
      });
      return response.data;
    }
  } catch (error) {
    dispatch({
      type: "DELETE_SALE_FAILURE",
      payload: error.response?.data?.message || "Error deleting sale"
    });
    throw error;
  }
};
// Fetch daily price_achat totals
export const fetchDailyPriceAchat = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_DAILY_PRICE_ACHAT_REQUEST" });

    const response = await axios.get(
      `${server}/sales/daily-price-achat/${shopId}`,
      getConfig()
    );

    dispatch({
      type: "FETCH_DAILY_PRICE_ACHAT_SUCCESS",
      payload: response.data.totals,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: "FETCH_DAILY_PRICE_ACHAT_FAILURE",
      payload: error.response?.data?.message || "Error fetching daily totals",
    });
    throw error;
  }
};

// Fetch monthly price_achat totals
export const fetchMonthlyPriceAchat = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_MONTHLY_PRICE_ACHAT_REQUEST" });

    const response = await axios.get(
      `${server}/sales/monthly-price-achat/${shopId}`,
      getConfig()
    );

    dispatch({
      type: "FETCH_MONTHLY_PRICE_ACHAT_SUCCESS",
      payload: response.data.totals,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: "FETCH_MONTHLY_PRICE_ACHAT_FAILURE",
      payload: error.response?.data?.message || "Error fetching monthly totals",
    });
    throw error;
  }
};
export const fetchDailySales = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_DAILY_SALES_REQUEST" });

    const response = await axios.get(
      `${server}/sales/daily-sales/${shopId}`,
      getConfig()
    );

    dispatch({
      type: "FETCH_DAILY_SALES_SUCCESS",
      payload: response.data.totals,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: "FETCH_DAILY_SALES_FAILURE",
      payload: error.response?.data?.message || "Error fetching daily totals",
    });
    throw error;
  }
};

// Fetch monthly price_achat totals
export const fetchMonthlySales = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_MONTHLY_SALES_REQUEST" });

    const response = await axios.get(
      `${server}/sales/monthly-sales/${shopId}`,
      getConfig()
    );

    dispatch({
      type: "FETCH_MONTHLY_SALES_SUCCESS",
      payload: response.data.totals,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: "FETCH_MONTHLY_SALES_FAILURE",
      payload: error.response?.data?.message || "Error fetching monthly totals",
    });
    throw error;
  }
};

// Fetch sales for PDF report generation
export const fetchReportSales = (startDate, endDate, shopIds) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_REPORT_SALES_REQUEST" });

    const params = new URLSearchParams({ startDate, endDate });
    if (shopIds && shopIds.length > 0) {
      params.append("shopIds", shopIds.join(","));
    }

    const response = await axios.get(
      `${server}/sales/report?${params.toString()}`,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: "FETCH_REPORT_SALES_SUCCESS",
        payload: response.data.sales,
      });
      return response.data.sales;
    }
  } catch (error) {
    dispatch({
      type: "FETCH_REPORT_SALES_FAILURE",
      payload: error.response?.data?.message || "Error fetching report data",
    });
    throw error;
  }
};

// Fetch products interest analysis
export const fetchProductsInterestAnalysis = (shopId, dateRange) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_PRODUCTS_INTEREST_ANALYSIS_REQUEST" });

    const response = await axios.get(
      `${server}/sales/products-interest-analysis/${shopId}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: "FETCH_PRODUCTS_INTEREST_ANALYSIS_SUCCESS",
        payload: response.data.analysis
      });
      return response.data;
    }
  } catch (error) {
    dispatch({
      type: "FETCH_PRODUCTS_INTEREST_ANALYSIS_FAILURE",
      payload: error.response?.data?.message || "Error fetching analysis"
    });
    throw error;
  }
};

// Fetch products debt analysis
export const fetchProductsDebtAnalysis = (shopId) => async (dispatch) => {
  try {
    dispatch({ type: "FETCH_PRODUCTS_DEBT_ANALYSIS_REQUEST" });

    // Handle case where shopId is an object
    const shop = typeof shopId === 'object' ? shopId._id : shopId;

    const response = await axios.get(
      `${server}/sales/products-debt-analysis/${shop}`,
      getConfig()
    );

    if (response.data.success) {
      dispatch({
        type: "FETCH_PRODUCTS_DEBT_ANALYSIS_SUCCESS",
        payload: response.data.analysis
      });
      return response.data;  // Return the full response data
    }
  } catch (error) {
    console.error("Debt Analysis Error:", error);  // Add error logging
    dispatch({
      type: "FETCH_PRODUCTS_DEBT_ANALYSIS_FAILURE",
      payload: error.response?.data?.message || "Erreur lors de la récupération de l'analyse des dettes"
    });
    throw error;
  }
};